import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  FileText,
  Save,
  RotateCcw,
} from 'lucide-react';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { usePageTitle } from '@/shared/store/page';
import { useConsentBody, useUpdateConsentBody } from './api';

const MIN_LEN = 20;
const MAX_LEN = 50_000;

export function ConsentBodyEditPage(): JSX.Element {
  const { cups = '' } = useParams<{ cups: string }>();
  const { data, isLoading, isError } = useConsentBody(cups);
  const update = useUpdateConsentBody(cups);

  const [draft, setDraft] = useState<string>('');
  const [savedAt, setSavedAt] = useState<string | null>(null);

  usePageTitle(
    data?.nombre ?? 'Editar consentimiento',
    data !== undefined ? `CUPS ${data.cups}` : 'Cargando…',
  );

  // Sincroniza el draft cada vez que llega data nueva.
  useEffect(() => {
    if (data !== undefined) {
      setDraft(data.body);
    }
  }, [data?.body, data]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cargando consentimiento…</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (isError || data === undefined) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" /> Consentimiento no encontrado
          </CardTitle>
          <CardDescription>
            El CUPS <code className="font-mono">{cups}</code> no está en el catálogo.
          </CardDescription>
        </CardHeader>
        <div className="px-6 pb-6">
          <Button asChild variant="secondary">
            <Link to="/admin/consentimientos">
              <ArrowLeft className="w-4 h-4" /> Volver al listado
            </Link>
          </Button>
        </div>
      </Card>
    );
  }

  const isDirty = draft !== data.body;
  const lengthOk = draft.length >= MIN_LEN && draft.length <= MAX_LEN;
  const canSave = isDirty && lengthOk && !update.isPending;

  const onSave = async (): Promise<void> => {
    try {
      const updated = await update.mutateAsync({ body: draft });
      setSavedAt(updated.updatedAt);
    } catch {
      /* error visible vía update.error abajo */
    }
  };

  const onReset = (): void => {
    setDraft(data.body);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <Button asChild variant="ghost" size="sm">
                <Link to="/admin/consentimientos">
                  <ArrowLeft className="w-4 h-4" />
                </Link>
              </Button>
              <div className="min-w-0">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  {data.nombre}
                </CardTitle>
                <CardDescription>
                  CUPS <span className="font-mono">{data.cups}</span>
                  {data.archivoOriginal !== null && (
                    <span className="text-xs"> · origen: {data.archivoOriginal}</span>
                  )}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onReset}
                disabled={!isDirty || update.isPending}
              >
                <RotateCcw className="w-4 h-4" /> Descartar cambios
              </Button>
              <Button onClick={() => void onSave()} disabled={!canSave}>
                <Save className="w-4 h-4" />
                {update.isPending ? 'Guardando…' : 'Guardar cambios'}
              </Button>
            </div>
          </div>
        </CardHeader>

        {savedAt !== null && (
          <div className="mx-6 mb-3 px-3 py-2 rounded-md bg-success-soft border border-green-200 text-green-800 text-sm flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Cambios guardados. Aplican al próximo PDF generado para este procedimiento.
          </div>
        )}
        {update.isError && (
          <div className="mx-6 mb-3 px-3 py-2 rounded-md bg-danger-soft border border-red-200 text-red-800 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {update.error?.message ?? 'No fue posible guardar los cambios.'}
          </div>
        )}

        <div className="px-6 pb-6 space-y-3">
          <div className="flex justify-between text-xs text-text-muted">
            <span>
              Editor de texto plano. Los párrafos se separan con una línea en blanco; los
              títulos cortos se renderizan en negrita automáticamente.
            </span>
            <span
              className={
                lengthOk ? 'text-text-muted' : 'text-red-700 font-semibold'
              }
            >
              {draft.length.toLocaleString('es-CO')} / {MAX_LEN.toLocaleString('es-CO')}
            </span>
          </div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck
            className="w-full min-h-[600px] font-mono text-[13px] leading-relaxed p-4 border border-border rounded-md bg-bg-elevated focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="Escribe el cuerpo clínico del consentimiento…"
          />
          {!lengthOk && draft.length < MIN_LEN && (
            <div className="text-xs text-red-700">
              El cuerpo debe tener al menos {MIN_LEN} caracteres.
            </div>
          )}
        </div>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Vista previa (cómo se verá en el PDF)</CardTitle>
          <CardDescription>
            Renderizado aproximado del cuerpo. El PDF final agrega plantilla, encabezado
            del procedimiento, bloque de paciente/médico/anestesiólogo y firmas.
          </CardDescription>
        </CardHeader>
        <div className="px-6 pb-6">
          <article className="prose prose-sm max-w-none text-[14px] leading-relaxed text-text">
            {draft
              .split(/\n\n+/)
              .map((para, idx) => {
                const trimmed = para.trim();
                if (trimmed === '') return null;
                const isHeading =
                  trimmed.length <= 80 &&
                  !/[.;:]$/.test(trimmed) &&
                  /^[A-ZÁÉÍÓÚÑ][A-ZÁÉÍÓÚÑa-záéíóúñ ,()/\-]+$/.test(trimmed);
                if (isHeading) {
                  return (
                    <p key={idx} className="font-semibold text-primary-700 mt-3 mb-1">
                      {trimmed}
                    </p>
                  );
                }
                return (
                  <p key={idx} className="mb-2 whitespace-pre-line">
                    {trimmed}
                  </p>
                );
              })
              .filter(Boolean)}
          </article>
        </div>
      </Card>
    </div>
  );
}
